import { useSearchParams } from 'react-router-dom';
import { Icoon } from '../ui/Icoon';
import { Koppelingen } from './Koppelingen';
import { Abonnement } from './Abonnement';
import { HuisstijlScherm } from './Huisstijl';
import { AuditLog } from './AuditLog';
import { EigenVeldenBeheer } from './EigenVeldenBeheer';
import { Beveiliging } from './Beveiliging';
import { ApiBeheer } from './ApiBeheer';
import { useEffect, useState } from 'react';
import { STANDAARD_INSTELLINGEN, type Instellingen } from '@ep/core';
import { api } from '../lib/api';
import { useActie, useData } from '../lib/data';
import { AdresVeld, type GeoWaarde } from '../ui/AdresVeld';
import { getal, Laden, Tabs, Veld, VerwijderKnop } from '../ui/ui';

type Vorm = Record<keyof Instellingen, string>;
const NUM: (keyof Instellingen)[] = ['kennisWeging', 'bonusProject', 'aantalSuggesties', 'pauzeMin', 'pauzeVanaf', 'engUren', 'reisFactor', 'reisSnelheid', 'reisOpslag', 'eigenReistijd', 'prognoseMinUren', 'prognoseMaxEngineers', 'prognoseInzet'];

export function InstellingenScherm() {
  const { data: d } = useData();
  const [f, setF] = useState<Vorm | null>(null);
  const [kantoor, setKantoor] = useState<GeoWaarde | null>(null);
  useEffect(() => {
    if (!d || f) return;
    setF(Object.fromEntries(Object.entries(d.instellingen).map(([k, v]) => [k, v == null ? '' : String(v)])) as Vorm);
    setKantoor({ adres: d.instellingen.kantoorAdres ?? '', lat: d.instellingen.kantoorLat ?? null, lon: d.instellingen.kantoorLon ?? null, naam: null, gewijzigd: false });
  }, [d, f]);
  const opslaan = useActie(
    () => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(f!)) body[k] = NUM.includes(k as keyof Instellingen) ? getal(v) ?? (STANDAARD_INSTELLINGEN as never)[k] : v || null;
      body.kantoorAdres = kantoor!.adres || null;
      body.kantoorLat = kantoor!.gewijzigd ? kantoor!.lat : d!.instellingen.kantoorLat ?? null;
      body.kantoorLon = kantoor!.gewijzigd ? kantoor!.lon : d!.instellingen.kantoorLon ?? null;
      for (const k of ['taakVan', 'taakTot', 'outlookVan', 'outlookTot', 'engDagen', 'taakcodes']) if (!body[k]) delete body[k];
      return api.put('/api/instellingen', body);
    },
    { succes: 'Instellingen opgeslagen' },
  );
  const herstel = useActie(() => api.post('/api/instellingen/herstel'), { succes: 'Standaardwaarden teruggezet', opNa: () => setF(null) });
  if (!d || !f || !kantoor) return <Laden />;
  const v = (k: keyof Instellingen, label: string, hint?: string) => (
    <Veld label={label} hint={hint}>
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </Veld>
  );
  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1100 }}>
      <div className="rij">
        <h1 className="groei">Instellingen</h1>
        <VerwijderKnop label="Herstel standaard" onBevestig={() => herstel.mutate()} />
        <button className="knop primair" onClick={() => opslaan.mutate()}>Opslaan</button>
      </div>
      <div className="paneel">
        <h2 style={{ marginBottom: 10 }}>Slimme suggestie</h2>
        <div className="grid3">
          {v('kennisWeging', 'Weging kennisniveau (%)', 'De rest is beschikbaarheid')}
          {v('bonusProject', 'Bonus zelfde project', 'Punten voor wie al aan het project werkt')}
          {v('aantalSuggesties', 'Aantal suggesties')}
        </div>
      </div>
      <div className="paneel">
        <h2 style={{ marginBottom: 10 }}>Tijden en uren</h2>
        <div className="grid3">
          {v('taakVan', 'Standaard taak van')}
          {v('taakTot', 'Standaard taak tot')}
          {v('pauzeMin', 'Pauze (minuten)')}
          {v('pauzeVanaf', 'Pauze vanaf (uren per dag)')}
          {v('engUren', 'Uren per dag nieuwe engineer')}
          {v('engDagen', 'Werkdagen nieuwe engineer', 'bijv. ma,di,wo,do,vr')}
          {v('outlookVan', 'Outlook telt als bezet vanaf')}
          {v('outlookTot', 'Outlook telt als bezet tot')}
        </div>
      </div>
      <div className="paneel">
        <h2 style={{ marginBottom: 10 }}>Uitvoering, urenlink en TOPdesk</h2>
        <div className="grid2">
          <Veld label="Taakcodes (één per regel: code - omschrijving)" hint="Codes die op remote/onsite eindigen (of 1014/1015/8734/8735) bepalen automatisch de werkvorm">
            <textarea value={f.taakcodes} onChange={(e) => setF({ ...f, taakcodes: e.target.value })} style={{ minHeight: 120 }} />
          </Veld>
          <div className="kolom">
            {v('urenlinkSjabloon', 'Urenlink (sjabloon)', 'Bijv. https://…/uren?project={projectnummer}&code={taakcode}. Ook {changenr} en {changenrCompact}.')}
            {v('topdeskSjabloon', 'TOPdesk-link (sjabloon)', 'Bijv. https://jouw.topdesk.net/tas/secure/…{changenrCompact}')}
          </div>
        </div>
      </div>
      <div className="paneel">
        <h2 style={{ marginBottom: 10 }}>Reistijd</h2>
        <div className="grid3">
          <div className="span3"><AdresVeld label="Kantooradres (vertrekpunt voor engineers met 'vertrek vanaf kantoor')" waarde={kantoor} onChange={setKantoor} /></div>
          {v('reisFactor', 'Reisfactor (hemelsbreed → weg)')}
          {v('reisSnelheid', 'Gemiddelde snelheid (km/u)')}
          {v('reisOpslag', 'Opslag per rit (min)')}
          {v('eigenReistijd', 'Eigen reistijd per rit (min)', 'Telt niet als werktijd')}
        </div>
      </div>
      <div className="paneel">
        <h2 style={{ marginBottom: 10 }}>Prognose</h2>
        <div className="grid3">
          {v('prognoseMinUren', 'Minimaal vrij blok (uren)', 'Kleinere restjes tellen niet mee')}
          {v('prognoseMaxEngineers', 'Max. engineers per expertise', '0 = iedereen')}
          {v('prognoseInzet', 'Inzet op het project (%)')}
        </div>
      </div>
    </div>
  );
}

/** Beheer › Instellingen: algemeen, Outlook en processen (v2.5.0), abonnement (v2.6.0, SPEC §12.5) en audit-log (v2.11.1, SPEC §16.2). */
export function InstellingenPagina() {
  const [zp, setZp] = useSearchParams();
  const t = zp.get('tab');
  const tab = t === 'koppelingen' || t === 'abonnement' || t === 'huisstijl' || t === 'audit' || t === 'velden' || t === 'api' || t === 'beveiliging' ? t : 'algemeen';
  return (
    <>
      <div className="pagina" style={{ paddingBottom: 0, maxWidth: 1200 }}>
        <Tabs
          waarde={tab}
          onChange={(t) => setZp(t === 'algemeen' ? {} : { tab: t }, { replace: true })}
          opties={[
            ['algemeen', <><Icoon naam="settings" maat={16} /> Algemeen</>],
            ['koppelingen', <><Icoon naam="link" maat={16} /> Outlook en processen</>],
            ['velden', <><Icoon naam="sliders-horizontal" maat={16} /> Eigen velden</>],
            ['api', <><Icoon naam="webhook" maat={16} /> API en webhooks</>],
            ['beveiliging', <><Icoon naam="shield-check" maat={16} /> Beveiliging</>],
            ['huisstijl', <><Icoon naam="palette" maat={16} /> Huisstijl</>],
            ['abonnement', <><Icoon naam="credit-card" maat={16} /> Abonnement</>],
            ['audit', <><Icoon naam="history" maat={16} /> Audit-log</>],
          ]}
        />
      </div>
      {tab === 'koppelingen' ? <Koppelingen /> : tab === 'abonnement' ? <Abonnement /> : tab === 'huisstijl' ? <HuisstijlScherm /> : tab === 'audit' ? <AuditLog /> : tab === 'velden' ? <EigenVeldenBeheer /> : tab === 'api' ? <ApiBeheer /> : tab === 'beveiliging' ? <Beveiliging /> : <InstellingenScherm />}
    </>
  );
}
